import { WHATSAPP_NUMBER } from "@/lib/whatsapp";
import PolicyPage from "@/components/PolicyPage";

const Shipping = () => (
  <PolicyPage
    testId="shipping-page"
    overline="Customer care"
    title="Shipping &amp; Delivery"
    updated="February 15, 2026"
    description="Luxsova shipping timelines, charges, tracking, and what to do if your order arrives damaged."
  >
    <p>
      Every Luxsova order is packed by hand — dust-bagged, tissue-wrapped and boxed for
      shelf-worthy unboxing. Here&apos;s everything you need to know about how it reaches you.
    </p>

    <h2>1. Dispatch timelines</h2>
    <ul>
      <li><strong>Ready-to-ship inventory:</strong> dispatched within 24–48 working hours of confirmed payment.</li>
      <li><strong>Sourced-on-demand pieces:</strong> 5–10 working days for procurement + QC, then dispatched.</li>
      <li>Orders placed on weekends or public holidays begin processing on the next working day.</li>
    </ul>

    <h2>2. Delivery time by region</h2>
    <ul>
      <li><strong>Metros (Delhi NCR, Mumbai, Bengaluru, Hyderabad, Chennai, Kolkata, Pune, Ahmedabad):</strong> 2–4 working days after dispatch.</li>
      <li><strong>Tier-2 &amp; Tier-3 cities:</strong> 4–7 working days after dispatch.</li>
      <li><strong>Remote / North-East / hill states:</strong> 6–10 working days after dispatch.</li>
      <li><strong>International (available on request):</strong> 7–14 working days after dispatch. Duties and taxes at destination are payable by the recipient.</li>
    </ul>
    <p className="text-sm text-neutral-500">
      Timelines are indicative; occasional courier delays due to weather, strikes or force majeure
      are outside our control. We will proactively update you on WhatsApp if there is a delay of
      more than 48 hours.
    </p>

    <h2>3. Shipping charges</h2>
    <ul>
      <li><strong>Free shipping</strong> on all orders within India — no minimum order value.</li>
      <li><strong>Partial COD</strong> available on select PIN codes — pay a small advance online, balance on delivery.</li>
      <li>International shipping is quoted per country on request — write to us on WhatsApp for a rate.</li>
    </ul>

    <h2>4. Tracking your order</h2>
    <p>
      Once your parcel is picked up by the courier, you will receive an SMS + WhatsApp message
      with the tracking link. You can also log in to your account and open the order to see the
      live status. If you don&apos;t see an update for 48 hours after dispatch, ping us — we will
      chase the courier for you.
    </p>

    <h2>5. Packaging &amp; unboxing</h2>
    <ul>
      <li>Items ship inside their original dust bag (where provided), wrapped in acid-free tissue.</li>
      <li>Handbags and delicate items go inside a rigid shipper carton with foam / air-cushion inserts.</li>
      <li>Every parcel is sealed with tamper-evident tape and stamped with our QC signature.</li>
      <li>Please record an unboxing video for high-value pieces — it helps us process claims faster in the rare case of transit damage.</li>
    </ul>

    <h2>6. Damaged, missing or wrong item</h2>
    <p>
      In the unlikely event that your order arrives damaged, has a missing item, or is not what
      you ordered:
    </p>
    <ol>
      <li>Refuse the parcel at the door if possible, OR accept it and note the damage on the courier POD.</li>
      <li>Message us on <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer">WhatsApp</a> within 48 hours of delivery with your order number and clear photos / video.</li>
      <li>We will arrange a free pickup within 24 hours and dispatch a replacement or process a full refund, per your choice.</li>
    </ol>

    <h2>7. Address changes &amp; failed deliveries</h2>
    <ul>
      <li>Address changes can be made <strong>before dispatch</strong> — ping us on WhatsApp with your order number.</li>
      <li>If the courier is unable to deliver after 3 attempts (locked door, wrong address, refusal), the parcel returns to our warehouse. We&apos;ll refund the item value minus a flat ₹200 handling fee to cover forward + return logistics.</li>
    </ul>

    <h2>8. Returns &amp; refunds</h2>
    <p>
      Returns and refunds are covered separately in our <a href="/returns">Returns &amp; Refunds</a> policy.
    </p>

    <h2>9. Contact</h2>
    <p>
      Any shipping question? <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer">Message us on WhatsApp</a>{" "}
      or email <a href="mailto:care@luxsova.com">care@luxsova.com</a> — we typically reply
      within a couple of hours during business hours.
    </p>
  </PolicyPage>
);

export default Shipping;
